import {
  useState,
  useRef,
  useMemo
} from 'react';
import { invalidate } from '@react-three/fiber';
import {
  MineModelDto,
  ProjectDto,
  removeAddedNodeMutation,
  useDeletePoursMutation,
  useGetRelevantStopePoursQuery,
  useRestorePourMutation,
  StopeLocationDto
} from 'providers/api';
import { useConfirmation } from 'providers/confirm';
import { usePage } from 'providers/page';
import { useScenario } from 'modules/project/providers/scenario';
import { UseQueryResult } from 'react-query';
import { PCNode } from '../../MineModel/Nodes';
import PCSpool from '../../MineModel/Spools/PCSpool';
import { flowLoopMetresByNodeId } from '../../MineModel/Spools';
import { Model } from '../../hooks/useModelState';
import { AlternativeNodeColour } from '../../MineModel/Nodes/PCNode';

// A flow loop is a length of pipe coiled into an edge, so it lives on the spool. A node counts as a
// loop node when one of its incident spools carries a choke length — the same rule NodeOverlays draws by,
// via the same helper, so every protected node is a node the user can see carries a loop.
const buildFlowLoopNodeIds = (spools: PCSpool[]): Set<string> => new Set(Object.keys(flowLoopMetresByNodeId(spools)));

const validateNodesForDeletion = (nodes: PCNode[], canDeleteProtectedNodes: boolean, flowLoopNodeIds: Set<string>): string[] => {
  const validationErrors: string[] = [];
  nodes.forEach((node) => {
    if (!canDeleteProtectedNodes) {
      if (node.isStope) validationErrors.push(`Node '${node.id}' is a stope and cannot be deleted.`);
      if (node.isPit) validationErrors.push(`Node '${node.id}' is a pit and cannot be deleted.`);
      if (node.isPlantNode) validationErrors.push(`Node '${node.id}' is the backfill plant and cannot be deleted.`);
      if (flowLoopNodeIds.has(node.id)) validationErrors.push(`Node '${node.id}' is a loop and cannot be deleted.`);
      if (node.boosterPressure > 0) validationErrors.push(`Node '${node.id}' is a booster pump and cannot be deleted.`);
    }
  });
  return validationErrors;
};

const hasProtectedNodes = (nodes: PCNode[], flowLoopNodeIds: Set<string>): boolean => nodes.some(
  (node) => node.isStope || node.isPit || node.isPlantNode || flowLoopNodeIds.has(node.id),
);

const hasStopes = (nodes: PCNode[]): boolean => nodes.some((node) => node.isStope);

interface UseDeleteNodesProps {
  model: Model;
  project: ProjectDto;
  mineModel: MineModelDto;
  mineModelQuery: UseQueryResult<MineModelDto | undefined, unknown>;
  canDeleteProtectedNodes?: boolean;
}
const useDeleteNodes = ({
  model,
  project,
  mineModel,
  mineModelQuery,
  canDeleteProtectedNodes = false,
}: UseDeleteNodesProps) => {
  const [isSelectingNodes, setIsSelectingNodes] = useState(false);
  const isSelectingNodesRef = useRef(false);
  const [selectedNodes, setSelectedNodes] = useState<PCNode[]>([]);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const hasSelectedStopes = selectedNodes.some((node) => node.isStope);
  const removeAddedNodes = removeAddedNodeMutation();
  const deletePoursMutation = useDeletePoursMutation();
  const restorePourMutation = useRestorePourMutation();
  const { activeScenarioId } = useScenario();
  const relevantStopePoursQuery = useGetRelevantStopePoursQuery(
    {
      projectId: project.entityId as string,
      page: 1,
      pageSize: Math.max(mineModel?.reticulationData?.stopes?.length ?? 0, 1),
      scenarioId: activeScenarioId ?? undefined,
    },
    { enabled: hasSelectedStopes },
  );
  const confirm = useConfirmation();
  const {
    actions: {
      setUndoActionOverride,
      clearUndoActionOverride,
    },
  } = usePage();
  const [deleteErrors, setDeleteErrors] = useState<string[]>([]);
  const flowLoopNodeIds = useMemo(() => buildFlowLoopNodeIds(Object.values(model.spools)), [model.spools]);

  const getStopeIdsForNodes = (nodes: PCNode[]): string[] => {
    const stopeNodes = nodes.filter((node) => node.isStope);
    const stopeLocations = mineModel?.reticulationData?.stopeLocations ?? [];
    return stopeNodes
      .map((node) => stopeLocations.find((sl: StopeLocationDto) => sl.nodeId === node.id)?.stopeId)
      .filter((stopeId): stopeId is string => !!stopeId);
  };

  const handleNodeSelect = (node: PCNode | undefined) => {
    if (!isSelectingNodes || !model || !node) return;

    const nodeInModel = model.nodes[node.id] as PCNode | undefined;
    if (!nodeInModel) return;

    const isCurrentlySelected = selectedNodes.some((n) => n.id === node.id);

    if (isCurrentlySelected) {
      model.setNodeColour(node.id, AlternativeNodeColour.DEFAULT);
      const nextSelectedNodes = selectedNodes.filter((n) => n.id !== node.id);
      setSelectedNodes(nextSelectedNodes);
      setDeleteErrors(validateNodesForDeletion(nextSelectedNodes, canDeleteProtectedNodes, flowLoopNodeIds));
      requestAnimationFrame(() => {
        invalidate();
      });
    } else {
      model.setNodeColour(node.id, AlternativeNodeColour.DELETE);
      const nextSelectedNodes = [...selectedNodes, nodeInModel];
      setSelectedNodes(nextSelectedNodes);
      setDeleteErrors(validateNodesForDeletion(nextSelectedNodes, canDeleteProtectedNodes, flowLoopNodeIds));
      requestAnimationFrame(() => {
        invalidate();
      });
    }
  };

  const handleBulkNodeSelect = (nodes: PCNode[]) => {
    if (!isSelectingNodesRef.current || !model) return;

    setSelectedNodes((prevSelectedNodes) => {
      const selectedNodeIds = new Set(prevSelectedNodes.map((n) => n.id));
      const newNodes = nodes.filter((node) => {
        const nodeInModel = model.nodes[node.id] as PCNode | undefined;
        if (!nodeInModel) return false;
        if (selectedNodeIds.has(node.id)) return false;
        selectedNodeIds.add(node.id);
        return true;
      });

      model.setNodeColours(newNodes.map((node) => node.id), AlternativeNodeColour.DELETE);

      const nextSelectedNodes = [...prevSelectedNodes, ...newNodes.map((n) => model.nodes[n.id] as PCNode)];
      setDeleteErrors(validateNodesForDeletion(nextSelectedNodes, canDeleteProtectedNodes, flowLoopNodeIds));
      return nextSelectedNodes;
    });
    requestAnimationFrame(() => {
      invalidate();
    });
  };

  const handleSelectForDeleteNode = () => {
    setDeleteErrors([]);
    setIsSelectingNodes(true);
    isSelectingNodesRef.current = true;
    setSelectedNodes([]);
  };

  const publishUndoForDeletedPours = (deletedPourIds: string[]) => {
    const uniqueDeletedPourIds = Array.from(new Set(deletedPourIds));
    if (uniqueDeletedPourIds.length === 0) {
      return;
    }

    const handleUndo = () => {
      setUndoActionOverride((current) => (current ? {
        ...current,
        enabled: false,
        loading: true,
      } : current));

      Promise.allSettled(uniqueDeletedPourIds.map((pourId) => restorePourMutation.mutateAsync(pourId)))
        .then(async (results) => {
          const hasErrors = results.some((result) => result.status === 'rejected');
          if (hasErrors) {
            setUndoActionOverride({
              enabled: true,
              loading: false,
              onClick: handleUndo,
            });
            return;
          }

          await mineModelQuery.refetch();
          clearUndoActionOverride();
        })
        .catch(() => {
          setUndoActionOverride({
            enabled: true,
            loading: false,
            onClick: handleUndo,
          });
        });
    };

    setUndoActionOverride({
      enabled: true,
      loading: false,
      onClick: handleUndo,
    });
  };

  const deletePoursForStopes = async (stopeIds: string[]): Promise<string[]> => {
    const stopeIdsSet = new Set(stopeIds);
    const data = relevantStopePoursQuery.data ?? (await relevantStopePoursQuery.refetch()).data;
    const pourIds = (data?.items ?? [])
      .filter((stopePours) => stopeIdsSet.has(stopePours.stopeSummary?.stopeIdentifier?.stopeId ?? ''))
      .flatMap((stopePours) => stopePours.pours ?? [])
      .map((pour) => pour.entityId);

    if (pourIds.length === 0) {
      return [];
    }

    await deletePoursMutation.mutateAsync({ pourIds });
    return pourIds;
  };

  const executeNodeDeletion = async () => {
    setIsDeleting(true);
    let deletedPourIds: string[] = [];

    try {
      const stopeIds = getStopeIdsForNodes(selectedNodes);
      if (stopeIds.length > 0) {
        deletedPourIds = await deletePoursForStopes(stopeIds);
      }

      // Deleting a node deletes its edges. The server prunes them in the same save, so no second
      // request here - this only keeps the optimistic client state consistent with what will come back.
      model.removeNodes(selectedNodes);
      const allSpools = Object.values(model.spools);
      const removedSpools = Array.from(new Map(
        selectedNodes
          .flatMap((node) => model.findSpoolsByNodeId(allSpools, node.id))
          .map((spool) => [spool.spoolId, spool] as [string, PCSpool]),
      ).values());
      model.removeSpools(removedSpools);

      removeAddedNodes.mutate(
        {
          projectId: project.entityId as string,
          nodeId: selectedNodes.map((n) => n.id),
          projectName: project.name,
          data: [],
          forceDelete: canDeleteProtectedNodes,
        },
        {
          onSuccess: async () => {
            await mineModelQuery.refetch();
            publishUndoForDeletedPours(deletedPourIds);
            setIsDeleteDialogOpen(false);
            setSelectedNodes([]);
            setIsDeleting(false);
            setIsSelectingNodes(false);
            isSelectingNodesRef.current = false;
          },
          onError: async () => {
            if (deletedPourIds.length > 0) {
              await Promise.allSettled(deletedPourIds.map((pourId) => restorePourMutation.mutateAsync(pourId)));
            }

            model.addNodes(selectedNodes);
            model.addSpools(removedSpools);
            const resetNodes = selectedNodes.map((node) => ({
              ...node,
              nodeColour: AlternativeNodeColour.DEFAULT,
            }));
            setSelectedNodes(resetNodes);
            setIsDeleting(false);
          },
        },
      );
    } catch {
      if (deletedPourIds.length > 0) {
        await Promise.allSettled(deletedPourIds.map((pourId) => restorePourMutation.mutateAsync(pourId)));
      }

      setIsDeleting(false);
    }
  };

  const handleDeleteNode = () => {
    if (isDeleting) return;

    if (deleteErrors.length > 0) {
      setIsDeleteDialogOpen(true);
      return;
    }

    if (selectedNodes.length === 0) return;

    const containsStopes = hasStopes(selectedNodes);
    const containsProtectedNodes = hasProtectedNodes(selectedNodes, flowLoopNodeIds);
    let description = `Are you sure you want to remove ${selectedNodes.length} selected node(s)? This action cannot be undone.`;

    if (containsStopes) {
      description = `Warning: You are about to delete ${selectedNodes.length} node(s) including stope(s). `
        + 'All stope data and associated pours will be permanently deleted. This action cannot be undone.';
    } else if (containsProtectedNodes) {
      description = `Warning: You are about to delete ${selectedNodes.length} node(s) including protected nodes `
        + '(pits, loops, or backfill plants). This action cannot be undone.';
    }

    confirm({
      variant: 'danger',
      description,
    }).then(() => {
      executeNodeDeletion();
    });
  };

  const handleCancelSelection = () => {
    model.setNodeColours(selectedNodes.map((node) => node.id), AlternativeNodeColour.DEFAULT);
    setSelectedNodes([]);
    setDeleteErrors([]);
    setIsSelectingNodes(false);
    isSelectingNodesRef.current = false;
    requestAnimationFrame(() => {
      invalidate();
    });
  };

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false);
    setDeleteErrors([]);
  };

  return {
    isSelectingNodes,
    selectedNodes,
    isDeleteDialogOpen,
    setIsDeleteDialogOpen,
    handleSelectForDeleteNode,
    handleDeleteNode,
    handleCancelSelection,
    handleCloseDialog,
    deleteErrors,
    handleNodeSelect,
    handleBulkNodeSelect,
    isDeleting,
  };
};

export default useDeleteNodes;
